import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';

import Button from '../components/ui/Button/Button';
import { projectService, type Project } from '../services/projectService';
import './Report.css';

const getProjectTimestamp = (project: Project) => {
  const timestampSource = project.submission_date || project.updated_at || project.created_at;
  const parsedTimestamp = new Date(timestampSource).getTime();

  return Number.isNaN(parsedTimestamp) ? 0 : parsedTimestamp;
};

const getLatestReportProject = (projects: Project[]) => {
  const projectsWithSubmissions = projects.filter((project) => project.latest_submission_id);
  const candidates = projectsWithSubmissions.length > 0 ? projectsWithSubmissions : projects;

  return [...candidates].sort(
    (firstProject, secondProject) =>
      getProjectTimestamp(secondProject) - getProjectTimestamp(firstProject),
  )[0];
};

const Report = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const latestProject = useMemo(() => getLatestReportProject(projects), [projects]);

  useEffect(() => {
    const loadLatestProject = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const loadedProjects = await projectService.getProjects();
        setProjects(loadedProjects);
      } catch (loadError) {
        console.error('Failed to load latest report project:', loadError);
        setError('Failed to load your latest report.');
      } finally {
        setIsLoading(false);
      }
    };

    void loadLatestProject();
  }, []);

  useEffect(() => {
    if (!isLoading && latestProject) {
      navigate(`/report/${latestProject.id}`, { replace: true });
    }
  }, [isLoading, latestProject, navigate]);

  if (isLoading || latestProject) {
    return (
      <div className="report-page">
        <div className="report-loading">
          <Loader2 className="animate-spin text-[#181C62]" size={36} strokeWidth={2.4} />
          <p className="mt-4 text-gray-600">Opening your latest report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-page">
        <div className="report-error">
          <AlertCircle className="text-red-600" size={28} strokeWidth={2.4} />
          <p className="text-red-600">{error}</p>
          <Button variant="blue" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-page">
      <div className="report-error">
        <p className="text-[var(--color-grey-55)]">
          No project reports are available yet.
        </p>
        <Button variant="blue" onClick={() => navigate('/project')}>
          Go to Projects
        </Button>
      </div>
    </div>
  );
};

export default Report;
